import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUserData from "../hooks/useUserData";
import {
  Typography,
  CircularProgress,
  Drawer,
  Button,
  ButtonGroup,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Box,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Treemap,
} from "recharts";
import { getCachedRoster } from "./RosterCache";

// Custom tooltip for charts
const CustomTooltip = ({ active, payload }) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          padding: 8,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 4,
        }}
      >
        {`${name}: ${value.toLocaleString()}`}
      </div>
    );
  }
  return null;
};

const TreemapCheckboxIcon = () => (
  <span className="proddy-checkbox-icon" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 18 18">
      <rect
        x="2"
        y="2"
        width="14"
        height="14"
        rx="2"
        ry="2"
        fill="none"
        stroke="var(--textMuted)"
        strokeWidth="2"
      />
    </svg>
  </span>
);

const TreemapCheckboxCheckedIcon = () => (
  <span className="proddy-checkbox-icon" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 18 18">
      <rect
        x="2"
        y="2"
        width="14"
        height="14"
        rx="2"
        ry="2"
        fill="var(--teamColor)"
        stroke="var(--teamColor)"
        strokeWidth="2"
      />
      <path
        d="M5 9.5l2.5 2.5L13 6.5"
        fill="none"
        stroke="var(--textPrimary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const TeamProddy = () => {
  const { userData, loading } = useUserData();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // state
  const [proddyData, setProddyData] = useState({});
  const [roster, setRoster] = useState([]);
  const [loadingProddy, setLoadingProddy] = useState(true);
  const [loadingRoster, setLoadingRoster] = useState(true);

  const [viewBy, setViewBy] = useState("resource");
  const [timeWindow, setTimeWindow] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [chartType, setChartType] = useState("pie");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hiddenTreemap, setHiddenTreemap] = useState(new Set());

  // fetch & poll
  useEffect(() => {
    if (loading) return;
    if (!userData) return navigate("/starsonata");
    if (userData.teamId === "unassigned") return navigate("/join");
    if (!userData.verified) return navigate("/pending");

    let isMounted = true;
    setLoadingProddy(true);

    const fetchProd = () => {
      if (!isMounted) return;
      let url = `https://imp.slypandainteractive.com/proddy.php?team_id=${userData.teamId}`;
      if (timeWindow) url += `&window=${timeWindow}`;
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (isMounted) {
            setProddyData(data);
            setLastUpdated(new Date());
          }
        })
        .catch((err) => console.error("Error loading proddy data:", err))
        .finally(() => {
          if (isMounted) setLoadingProddy(false);
        });
    };

    fetchProd();
    const intervalId = setInterval(fetchProd, 120000);

    setLoadingRoster(true);
    getCachedRoster(userData.teamId)
      .then((r) => {
        if (isMounted) setRoster(r);
      })
      .finally(() => {
        if (isMounted) setLoadingRoster(false);
      });

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [loading, userData, navigate, timeWindow]);

  // flatten entries
  const flattenEntries = () => {
    const entries = [];
    const proc = (set) =>
      Object.entries(set).forEach(([resource, items]) => {
        // ignore any bogus entries with resource names over 50 chars
        if (resource.length > 50) {
          console.warn(`Skipping Dodgy Resource Name: ${resource}`);
          return;
        }
        items.forEach(({ bot_name, total_quantity }) => {
          entries.push({
            botName: bot_name,
            resource,
            quantity: Number(total_quantity),
          });
        });
      });

    if (proddyData.priority) proc(proddyData.priority);
    if (proddyData.others) proc(proddyData.others);

    return entries;
  };

  // merge alts -> mains
  const tableData = useMemo(() => {
    const merged = {};
    const altMap = {};
    const unknownPattern = /^TB\d{1,3}$/i;
    roster.forEach(({ mainCharacterName, altCharacters }) =>
      altCharacters.forEach((alt) => {
        altMap[alt.toLowerCase()] = mainCharacterName;
      })
    );
    flattenEntries().forEach(({ botName, resource, quantity }) => {
      const main =
        unknownPattern.test(botName)
          ? "Unknown Contributors"
          : altMap[botName.toLowerCase()] || botName;
      const key = `${main.toLowerCase()}::${resource.toLowerCase()}`;
      if (!merged[key]) merged[key] = { botName: main, resource, quantity: 0 };
      merged[key].quantity += quantity;
    });
    return Object.values(merged);
  }, [proddyData, roster]);

  // summaries
  const summaryByResource = useMemo(() => {
    const sums = {};
    tableData.forEach(({ resource, quantity }) => {
      sums[resource] = (sums[resource] || 0) + quantity;
    });
    return Object.entries(sums).map(([resource, total]) => ({
      key: resource,
      label: resource,
      total,
    }));
  }, [tableData]);

  const summaryByBot = useMemo(() => {
    const sums = {};
    tableData.forEach(({ botName, quantity }) => {
      sums[botName] = (sums[botName] || 0) + quantity;
    });
    return Object.entries(sums).map(([botName, total]) => ({
      key: botName,
      label: botName,
      total,
    }));
  }, [tableData]);

  const items = viewBy === "resource" ? summaryByResource : summaryByBot;
  const COLORS = [
    theme.palette.primary.light,
    theme.palette.secondary.light,
    theme.palette.error.light,
    theme.palette.warning.light,
    theme.palette.success.light,
  ];

  const toggleStyles = (active) =>
    active
      ? {
          backgroundColor: "var(--teamColor)",
          color: "var(--textPrimary)",
        }
      : {
          backgroundColor: "var(--panelBg)",
          color: "var(--textPrimary)",
          borderColor: "var(--teamColor)",
        };

  const chartData = openKey
    ? tableData
        .filter((e) =>
          viewBy === "resource" ? e.resource === openKey : e.botName === openKey
        )
        .map((e) => ({
          name: viewBy === "resource" ? e.botName : e.resource,
          value: e.quantity,
        }))
    : [];

  const visibleChartData = chartData.filter((item) => !hiddenTreemap.has(item.name));
  const totalValue = visibleChartData.reduce((sum, d) => sum + d.value, 0);

  // pie slice labels: show % instead of raw number
  const renderPercentLabel = ({ name, value }) => {
    if (!totalValue) return `${name}: 0%`;
    const pct = ((value / totalValue) * 100).toFixed(1);
    return `${name}: ${pct}%`;
  };

  const ProductionTreemap = ({ data, total }) => {
    const palette = ["#2f2f2f", "#343434", "#393939", "#3f3f3f", "#444444", "#4a4a4a"];
    const treemapData = data.map((item) => ({
      name: item.name,
      value: item.value,
    }));

    const TreemapContent = (props) => {
      const { x, y, width, height, index, name, payload } = props;
      if (width < 60 || height < 36) return null;
      const labelRaw =
        (typeof name === "string" && name) ||
        (typeof payload?.name === "string" && payload.name) ||
        "";
      const maxChars = Math.max(6, Math.floor((width - 12) / 7));
      const label =
        labelRaw.length > maxChars ? `${labelRaw.slice(0, maxChars - 3)}...` : labelRaw;
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={palette[index % palette.length]}
            stroke="var(--teamColor)"
            strokeWidth={0.6}
          />
          <text x={x + 6} y={y + 18} fill="var(--textPrimary)" fontSize={12}>
            {label}
          </text>
        </g>
      );
    };

    const TreemapTooltip = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null;
      const { name, value } = payload[0].payload;
      const pct = total ? ((value / total) * 100).toFixed(1) : "0.0";
      return (
        <div className="proddy-treemap-tooltip">
          <div>{name}</div>
          <div>{value.toLocaleString()}</div>
          <div>{pct}%</div>
        </div>
      );
  };

  return (
      <div className="proddy-treemap">
        <ResponsiveContainer width="100%" height={320}>
          <Treemap
            data={treemapData}
            dataKey="value"
            nameKey="name"
            ratio={4 / 3}
            content={<TreemapContent />}
          >
            <Tooltip content={<TreemapTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>
    );
  };

  useEffect(() => {
    if (!openKey) return;
    setHiddenTreemap(new Set());
  }, [openKey]);

  // loading states
  if (loading || !userData) return <CircularProgress />;
  if (loadingProddy || loadingRoster)
    return <Typography align="center">Loading data...</Typography>;

  return (
    <div className="proddy-page">
      <Typography variant="h4" align="center" gutterBottom>
        Production Summary
      </Typography>

      <ButtonGroup sx={{ mb: 2 }}>
        <Button onClick={() => setViewBy("resource")} sx={toggleStyles(viewBy === "resource")}>
          By Resource
        </Button>
        <Button onClick={() => setViewBy("bot")} sx={toggleStyles(viewBy === "bot")}>
          By Player
        </Button>
      </ButtonGroup>

      <ButtonGroup sx={{ mb: 2, ml: 2 }}>
        {[
          { key: "", label: "All" },
          { key: "1h", label: "Last 1 h" },
          { key: "12h", label: "Last 12 h" },
          { key: "24h", label: "Last 24 h" },
          { key: "7d", label: "Last 7 days" },
          { key: "28d", label: "Last 28 days" },
        ].map(({ key, label }) => (
          <Button key={key || "all"} onClick={() => setTimeWindow(key)} sx={toggleStyles(timeWindow === key)}>
            {label}
          </Button>
        ))}
      </ButtonGroup>

      <div className="proddy-list proddy-list--centered">
        <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
          All {viewBy === "resource" ? "Resources" : "Players"}
        </Typography>
        <List className="proddy-list-grid">
          {items
            .slice()
            .sort((a, b) => b.total - a.total)
            .map(({ key, label, total }) => (
              <ListItem
                key={key}
                disableGutters
                className="proddy-list-item"
                onClick={() => {
                  setOpenKey(key);
                  setChartType("treemap");
                }}
              >
                <ListItemText
                  primary={label}
                  secondary={total.toLocaleString()}
                  primaryTypographyProps={{ color: "var(--textPrimary)", align: "center" }}
                  secondaryTypographyProps={{ color: "var(--textMuted)", align: "center" }}
                />
              </ListItem>
            ))}
        </List>
      </div>

      {lastUpdated && (
        <Typography align="center" sx={{ mt: 2, fontSize: "1rem" }} className="proddy-muted">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </Typography>
      )}

      <Drawer
        anchor="right"
        open={Boolean(openKey)}
        onClose={() => setOpenKey(null)}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "var(--surfaceBg)",
            borderLeft: "2px solid var(--teamColor)",
            width: isMobile ? "100%" : 480,
            maxWidth: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            backgroundColor: "var(--panelBg)",
            borderBottom: "1px solid var(--teamColor)",
          }}
        >
          <Typography variant="h6">{openKey} Breakdown</Typography>
          <IconButton
            aria-label="Close breakdown"
            onClick={() => setOpenKey(null)}
            size="small"
            sx={{
              color: "var(--textPrimary)",
              "&:focus-visible": { outline: "2px solid var(--teamColor)", outlineOffset: 2 },
            }}
          >
            <span aria-hidden="true">X</span>
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <ButtonGroup sx={{ mb: 2 }} size="small">
            <Button
              onClick={() => setChartType("treemap")}
              sx={{
                ...toggleStyles(chartType === "treemap"),
                "&:focus-visible": { outline: "2px solid var(--teamColor)", outlineOffset: 2 },
              }}
            >
              Treemap
            </Button>
            <Button
              onClick={() => setChartType("pie")}
              sx={{
                ...toggleStyles(chartType === "pie"),
                "&:focus-visible": { outline: "2px solid var(--teamColor)", outlineOffset: 2 },
              }}
            >
              Pie Chart
            </Button>
            <Button
              onClick={() => setChartType("bar")}
              sx={{
                ...toggleStyles(chartType === "bar"),
                "&:focus-visible": { outline: "2px solid var(--teamColor)", outlineOffset: 2 },
              }}
            >
              Bar Chart
            </Button>
            <Button
              onClick={() => setChartType("list")}
              sx={{
                ...toggleStyles(chartType === "list"),
                "&:focus-visible": { outline: "2px solid var(--teamColor)", outlineOffset: 2 },
              }}
            >
              List
            </Button>
          </ButtonGroup>

          {chartType === "treemap" && (
            <ProductionTreemap data={visibleChartData} total={totalValue} />
          )}

          {chartType === "pie" && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={visibleChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={renderPercentLabel}
                >
                  {visibleChartData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          )}

          {chartType === "bar" && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={visibleChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide={viewBy === "resource"} tick={{ fill: "var(--textPrimary)" }} />
                <YAxis tick={{ fill: "var(--textPrimary)" }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value">
                  {visibleChartData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          {chartType === "list" && (
            <List>
              {chartData.map(({ name, value }) => (
                <ListItem key={name} disableGutters sx={{ py: 1 }}>
                  <ListItemText
                    primary={name}
                    secondary={value.toLocaleString()}
                    primaryTypographyProps={{ fontWeight: "bold", color: "var(--textPrimary)" }}
                    secondaryTypographyProps={{ color: "var(--textMuted)" }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {chartType !== "list" && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "24px 1fr 140px",
                  gap: 2,
                  alignItems: "center",
                  px: 1,
                  pb: 1,
                  borderBottom: "1px solid var(--teamColor)",
                  color: "var(--textMuted)",
                  fontSize: "0.85rem",
                }}
              >
                <span />
                <span>Name</span>
                <span style={{ textAlign: "right" }}>Total</span>
              </Box>
              {chartData.map(({ name, value }) => (
                <Box
                  key={name}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "24px 1fr 140px",
                    alignItems: "center",
                    gap: 2,
                    px: 1,
                    py: 0.5,
                    color: "var(--textPrimary)",
                  }}
                >
                  <Checkbox
                    checked={!hiddenTreemap.has(name)}
                    onChange={() => {
                      setHiddenTreemap((prev) => {
                        const next = new Set(prev);
                        if (next.has(name)) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        return next;
                      });
                    }}
                    icon={<TreemapCheckboxIcon />}
                    checkedIcon={<TreemapCheckboxCheckedIcon />}
                    sx={{ padding: "2px" }}
                  />
                  <span>{name}</span>
                  <span style={{ color: "var(--textMuted)", textAlign: "right" }}>
                    {value.toLocaleString()}
                  </span>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export default TeamProddy;
